import clsx from "clsx";
import { observe } from "mobx";
import { inject, observer } from "mobx-react";
import Image from "next/image";
import { CSSProperties, RefObject, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Store } from "stores/store";
import { motion, useMotionValue, useTransform } from "framer-motion";
import ExportedImage from "next-image-export-optimizer";
import { getAltTextFromGeneralImage, getImageUrlFromGeneralImage } from "utils/imageHelpers";
import { cn, use4K, useTest4K } from "@/lib/utils";
import QrCode from "../shared/QrCode/QrCode";


interface ChatMessageImageProps {
    id: number;
    characterImageSrc: string;
    characterImageAlt: string;
    title: string;
    url: string;
    altText: string;
    text: string;
    cta_enlarge: string;
    cta_close: string;
    isMainChatMessage: boolean;
    store?: Store;
    isUser: boolean;
    disableTitle: boolean;
    timeToAppear?: Date;
    scrollRef: RefObject<HTMLDivElement>;
    isSelfie: boolean;
}

const ChatMessageImage: React.FunctionComponent<ChatMessageImageProps> = (props) => {
    if (!props.store) {
        throw Error("Store was not setup correctly");
    }

    const { updateChatMessageEnlarged, chatSteleLocalized, updateChatTimer, globalEventLocalized, updateChatImageId, chatImageId, chatMessageEnlarged } = props.store;

    const isOnline: boolean = process.env.NEXT_PUBLIC_IS_ONLINE === "true";
    const isStele: boolean = process.env.NEXT_PUBLIC_IS_STELE === "true";

    const fourK = useTest4K();
    const delay = props.timeToAppear ? Math.max(props.timeToAppear.getTime() - new Date().getTime(), 0) / 1000 : 0;

    // TODO: add the image markup with data from strapi

    const [enlarged, setEnlarged] = useState<boolean>(false);

    const variantsContainer = {
        hidden: { height: 0, opacity: 0 },
        shown: {
            height: "auto",
            opacity: 1,
            transition: { duration: 0.3, delay: delay, ease: "circInOut" },
        },
        enlarged: {
            height: "auto",
            opacity: 1,
        },
    };

    const variantsMessage = {
        shown: {
            width: "960px",
            transition: { duration: 0.3 },
        },
        enlarged: {
            width: "1904px",
            transition: { duration: 0.3, delay: 0 },
        },
    };

    const variantsMessageUsername = {
        shown: {
            opacity: 1,
            transition: { duration: 0.5 },
        },
        enlarged: {
            opacity: 0,
            transition: { duration: 0.5 },
        },
    };

    const variantsMessageImage = {
        shown: {
            borderRadius: fourK ? "32px" : "8px",
            transition: { duration: 0.5 },
        },
        enlarged: {
            borderRadius: fourK ? "64px" : "16px",
            transition: { duration: 0.5 },
        },
    };

    const variantsMessageCopy = {
        shown: {
            opacity: 1,
            height: "auto",
            marginTop: "32px",
            transition: { duration: 0.5 },
        },
        enlarged: {
            opacity: 0,
            height: 0,
            marginTop: 0,
            transition: { duration: 0.5 },
        },
    };

    const variantsPolaroid = {
        shown: {
            opacity: 1,
            height: fourK ? "324px" : "82",
            width: fourK ? "324px" : "82",
            transition: { duration: 0.5 },
        },
        enlarged: {
            opacity: 0,
            height: 0,
            width: 0,
            transition: { duration: 0.5 },
        },
    };

    const variantsMessageEnlargeIcon = {
        shown: {
            opacity: 1,
            transition: { duration: 0.5 },
        },
        enlarged: {
            opacity: 0,
            transition: { duration: 0.5 },
        },
    };

    const variantsMessageEnlargedCTAs = {
        shown: {
            opacity: 0,
            height: 0,
            transition: { duration: 0.5 },
        },
        enlarged: {
            opacity: 1,
            height: "auto",
            transition: { duration: 0.5 },
        },
    };

    const chatMessageRef = useRef<HTMLDivElement>(null);

    const yProgress = useMotionValue(0);
    const parentOffsetHeight = props.scrollRef.current?.offsetHeight ?? 0;

    const animate = () => {
        if (chatMessageRef.current) {
            const offsetBottom =
                parentOffsetHeight - chatMessageRef.current?.offsetTop - chatMessageRef.current?.offsetHeight;
            yProgress.set(offsetBottom + 2 * (props.scrollRef.current?.scrollTop ?? 0));
        }
        requestAnimationFrame(animate);
    };

    useEffect(() => {
        setTimeout(() => {
            animate();
        }, delay);
    }, []);

    const yProgressOpacity = useTransform(
        yProgress,
        // Map x from these values:
        [parentOffsetHeight / 2, parentOffsetHeight],
        // Into these values:
        [1, 0],
    );

    return (
        <motion.div
            variants={variantsContainer}
            initial="hidden"
            animate={enlarged ? "enlarged" : "shown"}
            ref={chatMessageRef}

            style={{ scale: yProgressOpacity, opacity: yProgressOpacity }}
        >
            <div
                className={clsx(
                    "animatedHeight flex w-full flex-row overflow-x-clip items-end justify-start gap-[8px] 4k:gap-[32px]",
                    props.isUser ? "justify-end" : "justify-start",
                )}
            >
                <motion.div
                    variants={variantsMessage}
                    initial="shown"
                    animate={enlarged ? "enlarged" : "shown"}
                    onClick={() => {
                        if (isStele && props.isSelfie) {
                            updateChatMessageEnlarged(!enlarged);
                            updateChatImageId(props.id.toString())
                            setEnlarged(!enlarged);
                        }
                    }}
                    className={clsx(
                        "w-full",
                        fourK && "flex flex-col",
                        fourK && !enlarged && "max-w-min",
                        enlarged && "items-center justify-center"
                    )}
                >
                    <div className="relative max-w-min">
                        {props.disableTitle === false ? (
                            <motion.div
                                variants={variantsMessageUsername}
                                initial="shown"
                                animate={enlarged ? "enlarged" : "shown"}
                                className={clsx(
                                    "text-bold-dynamic-petrol w-full font-frutigerRoman font-bold uppercase italic leading-[55%] tracking-[1px]",
                                    "px-[16px] pb-[8px] text-[16px]",
                                    "4k:px-[64px] 4k:pb-[32px] 4k:text-[48px]",
                                    props.isUser && "text-right",
                                )}
                            >
                                {props.isUser ? "Heute-Ich" : "ZUKUNFTs-Ich"}
                            </motion.div>
                        ) : (
                            <div className="h-[10px]" />
                        )}
                        <div
                            className={clsx(
                                enlarged
                                    ? "transition-all delay-0 duration-1000"
                                    : "bg-whiteShade-20 p-[8px] 4k:p-[32px] shadow-glass-effect transition-all delay-0 duration-1000 w-full 4k:w-[1500px] max-w-min",
                                props.isUser ? (fourK ? "chatBubbleRight" : "chatBubbleRightBYOD") : (fourK ? "chatBubble" : "chatBubbleBYOD"),
                                enlarged ? "rounded-[16px] 4k:rounded-[64px] 4k:mb-[50%]" : "",
                                (chatMessageEnlarged && chatImageId !== props.id.toString()) && "opacity-0 h-0 w-0"

                                // "group-hover:opacity-100",
                                // props.isMainChatMessage ? "opacity-100" : "opacity-40"
                            )}
                        >
                            <div className={clsx("bg-elevation-1 shadow-scroll flex flex-row gap-[8px] 4k:gap-[32px]")}>
                                <div className="w-full text-white">
                                    <motion.div
                                        variants={variantsMessageImage}
                                        initial="shown"
                                        animate={enlarged ? "enlarged" : "shown"}
                                        className={clsx("h-full w-full max-w-min overflow-hidden font-frutigerRoman 4k:text-[52px] text-[13px] flex flex-col", props.isSelfie && enlarged ? "bg-white" : "bg-none")}
                                    >
                                        <ExportedImage
                                            src={props.url}
                                            /*src={"https://neueleben-strapi.curious.zone/uploads/Placeholder_QR_Code_3514040426.png"}*/
                                            alt={props.altText}
                                            fill={true}
                                            className={clsx("relative min-w-[230px] rounded-[11px] 4k:rounded-[44px] max-w-[230px] 2xs:max-w-[360px] 4k:min-w-[800px] md:max-w-[490px]", props.isSelfie && "border-[4px] 4k:border-[16px] border-white")}
                                        />
                                        {isStele &&
                                            <motion.div
                                                variants={variantsMessageEnlargeIcon}
                                                initial="shown"
                                                animate={enlarged ? "enlarged" : "shown"}
                                                className="absolute right-[19.5px] top-[19.5px] 4k:right-[78px] 4k:top-[78px]"
                                            >
                                                {props.isSelfie ?

                                                    <svg width="82" height="82" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M12.5535 16.5061C12.4114 16.6615 12.2106 16.75 12 16.75C11.7894 16.75 11.5886 16.6615 11.4465 16.5061L7.44648 12.1311C7.16698 11.8254 7.18822 11.351 7.49392 11.0715C7.79963 10.792 8.27402 10.8132 8.55352 11.1189L11.25 14.0682V3C11.25 2.58579 11.5858 2.25 12 2.25C12.4142 2.25 12.75 2.58579 12.75 3V14.0682L15.4465 11.1189C15.726 10.8132 16.2004 10.792 16.5061 11.0715C16.8118 11.351 16.833 11.8254 16.5535 12.1311L12.5535 16.5061Z" fill="white" />
                                                        <path d="M3.75 15C3.75 14.5858 3.41422 14.25 3 14.25C2.58579 14.25 2.25 14.5858 2.25 15V15.0549C2.24998 16.4225 2.24996 17.5248 2.36652 18.3918C2.48754 19.2919 2.74643 20.0497 3.34835 20.6516C3.95027 21.2536 4.70814 21.5125 5.60825 21.6335C6.47522 21.75 7.57754 21.75 8.94513 21.75H15.0549C16.4225 21.75 17.5248 21.75 18.3918 21.6335C19.2919 21.5125 20.0497 21.2536 20.6517 20.6516C21.2536 20.0497 21.5125 19.2919 21.6335 18.3918C21.75 17.5248 21.75 16.4225 21.75 15.0549V15C21.75 14.5858 21.4142 14.25 21 14.25C20.5858 14.25 20.25 14.5858 20.25 15C20.25 16.4354 20.2484 17.4365 20.1469 18.1919C20.0482 18.9257 19.8678 19.3142 19.591 19.591C19.3142 19.8678 18.9257 20.0482 18.1919 20.1469C17.4365 20.2484 16.4354 20.25 15 20.25H9C7.56459 20.25 6.56347 20.2484 5.80812 20.1469C5.07435 20.0482 4.68577 19.8678 4.40901 19.591C4.13225 19.3142 3.9518 18.9257 3.85315 18.1919C3.75159 17.4365 3.75 16.4354 3.75 15Z" fill="white" />
                                                    </svg>
                                                    :
                                                    // <svg
                                                    //     width="82"
                                                    //     height="82"
                                                    //     viewBox="0 0 82 82"
                                                    //     fill="none"
                                                    //     xmlns="http://www.w3.org/2000/svg"
                                                    //     className="w-[20.5px] h-[20.5px] 4k:w-[82px] 4k:h-[82px]"
                                                    // >
                                                    //     <path
                                                    //         id="Vector"
                                                    //         d="M8.35714 13.5714C8.35714 10.6933 10.6933 8.35714 13.5714 8.35714H27.2857C28.3276 8.35714 29.3269 7.94324 30.0636 7.20649C30.8004 6.46974 31.2143 5.47049 31.2143 4.42857C31.2143 3.38665 30.8004 2.3874 30.0636 1.65065C29.3269 0.913901 28.3276 0.5 27.2857 0.5H13.5714C10.1047 0.5 6.7799 1.87716 4.32853 4.32853C1.87716 6.7799 0.5 10.1047 0.5 13.5714V27.2857C0.5 28.3276 0.913901 29.3269 1.65065 30.0636C2.3874 30.8004 3.38665 31.2143 4.42857 31.2143C5.47049 31.2143 6.46974 30.8004 7.20649 30.0636C7.94324 29.3269 8.35714 28.3276 8.35714 27.2857V13.5714ZM51.9364 1.65065C51.1996 2.3874 50.7857 3.38665 50.7857 4.42857C50.7857 5.47049 51.1996 6.46974 51.9364 7.20649C52.6731 7.94324 53.6724 8.35714 54.7143 8.35714H68.4286C71.3067 8.35714 73.6429 10.6933 73.6429 13.5714V27.2857C73.6429 28.3276 74.0568 29.3269 74.7935 30.0636C75.5303 30.8004 76.5295 31.2143 77.5714 31.2143C78.6133 31.2143 79.6126 30.8004 80.3494 30.0636C81.0861 29.3269 81.5 28.3276 81.5 27.2857V13.5714C81.5 10.1047 80.1228 6.7799 77.6715 4.32853C75.2201 1.87716 71.8953 0.5 68.4286 0.5H54.7143C53.6724 0.5 52.6731 0.913902 51.9364 1.65065ZM7.20649 51.9364C6.46974 51.1996 5.47049 50.7857 4.42857 50.7857C3.38665 50.7857 2.3874 51.1996 1.65065 51.9364C0.913902 52.6731 0.5 53.6724 0.5 54.7143V68.4286C0.5 71.8953 1.87716 75.2201 4.32853 77.6715C6.7799 80.1228 10.1047 81.5 13.5714 81.5H27.2857C28.3276 81.5 29.3269 81.0861 30.0636 80.3494C30.8004 79.6126 31.2143 78.6133 31.2143 77.5714C31.2143 76.5295 30.8004 75.5303 30.0636 74.7935C29.3269 74.0568 28.3276 73.6429 27.2857 73.6429H13.5714C10.6933 73.6429 8.35714 71.3067 8.35714 68.4286V54.7143C8.35714 53.6724 7.94324 52.6731 7.20649 51.9364ZM80.3494 51.9364C79.6126 51.1996 78.6133 50.7857 77.5714 50.7857C76.5295 50.7857 75.5303 51.1996 74.7935 51.9364C74.0568 52.6731 73.6429 53.6724 73.6429 54.7143V68.4286C73.6429 71.3067 71.3067 73.6429 68.4286 73.6429H54.7143C53.6724 73.6429 52.6731 74.0568 51.9364 74.7935C51.1996 75.5303 50.7857 76.5295 50.7857 77.5714C50.7857 78.6133 51.1996 79.6126 51.9364 80.3494C52.6731 81.0861 53.6724 81.5 54.7143 81.5H68.4286C71.8953 81.5 75.2201 80.1228 77.6715 77.6715C80.1228 75.2201 81.5 71.8953 81.5 68.4286V54.7143C81.5 53.6724 81.0861 52.6731 80.3494 51.9364Z"
                                                    //         fill="white"
                                                    //         stroke="white"
                                                    //     />
                                                    // </svg>
                                                    <></>
                                                }
                                            </motion.div>
                                        }

                                        {(props.isSelfie && enlarged) &&
                                            <motion.div
                                                variants={variantsPolaroid}
                                                initial="shown"
                                                animate={enlarged ? "shown" : "enlarged"}
                                                className="relative flex 4k:px-[64px] px-[16px] w-full 4k:gap-[64px] gap-[16px] items-center 4k:py-[32px] py-[8px]"
                                            >
                                                <div className="flex relative 4k:h-[260px] h-[65px] items-center  ">
                                                    <QrCode />
                                                </div>
                                                <p className="font-frutigerBold text-neutral-600 4k:text-[36px] text-[18px] break-words 4k:max-w-[960px] max-w-[240px] tracking-minus-2">
                                                    {globalEventLocalized?.selfieModalQrCodeText}
                                                </p>
                                            </motion.div>
                                        }

                                    </motion.div>
                                    <motion.div
                                        variants={variantsMessageCopy}
                                        initial="enlarged"
                                        animate={enlarged ? "enlarged" : "shown"}
                                        className="w-full font-frutigerRoman 4k:text-[52px] text-[13px]"
                                    >
                                        {props.text}
                                    </motion.div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <motion.div
                        variants={variantsMessageEnlargedCTAs}
                        initial="shown"
                        animate={enlarged ? "enlarged" : "shown"}
                        className="flex w-full justify-center font-frutigerRoman 4k:text-[52px] text-[13px]"
                    >
                        <div className="4k:mt-[32px] mt-[8px] flex w-full flex-row justify-center">
                            <button
                                disabled={!enlarged}
                                onClick={() => {
                                    updateChatTimer(false);
                                    updateChatMessageEnlarged(!enlarged);
                                    setEnlarged(!enlarged);
                                }}
                                className="flex flex-col"
                            >
                                <div
                                    className={clsx(
                                        "4k:text-[52px] text-[13px] font-black",
                                        "flex flex-row items-center justify-center",
                                        "4k:px-16 px-[16px] 4k:py-12 py-[12px] bg-grayShade-20 rounded-full",
                                        "font-frutigerRoman text-shadow-button-stele-primary-stele",
                                    )}
                                >
                                    {props.cta_close}
                                </div>
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
                {(!enlarged && props.isSelfie && fourK) &&
                    <div className={clsx("relative", (chatMessageEnlarged && chatImageId !== props.id.toString()) && "opacity-0 h-0 w-0")}>
                        <QrCode />
                    </div>
                }
            </div>

        </motion.div >
    );
};

export default inject("store")(observer(ChatMessageImage));
